<template>
  <div class="desktop" @click="closeContextMenu">
    <DesktopIcon
      v-for="icon in icons"
      :key="icon.id"
      :icon="icon"
      :all-icons="icons"
      :onShowContextMenu="showContextMenu"
      :onOpenApp="openApp"
    />

    <AppWindow
      v-for="win in openWindows"
      :key="win.id"
      :app="win"
      :ref="setWindowRef(win.id)" 
      v-show="!win.minimized && !win.closed"
      :onClose="closeApp"
      :onMinimize="toggleWindow"
    />

    <ContextMenu
      v-if="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :icon="contextMenu.icon"
      :onClose="closeContextMenu"
      :onDeleteIcon="deleteIcon"
      :onShowConfirmDialog="showConfirmDialog"
    />

    <SelectionBox :icons="icons" @selection-change="onSelectionChange" />
    <Taskbar
      :windows="openWindows"
      :active-id="activeWindowId"
      @toggle-window="toggleWindow"
      @bring-to-front="bringToFront"
      :onClickStart="showNotOsErrorDialog"
    />

    <ConfirmDialog
      v-if="confirmDialog.visible"
      :message="confirmDialog.message"
      :onConfirm="handleConfirmDialog"
      :onCancel="closeConfirmDialog"
    />

    <ErrorDialog
      v-if="showErrorDialog"
      :message="errorMessage"
      :onCancel="closeErrorDialog"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { onMounted, onBeforeUnmount } from 'vue';
import DesktopIcon from './components/DesktopIcon.vue';
import AppWindow from './components/AppWindow.vue';
import ContextMenu from './components/ContextMenu.vue';
import Taskbar from './components/Taskbar.vue';
import SelectionBox from './components/SelectionBox.vue';
import ConfirmDialog from './components/ConfirmDialog.vue';
import ErrorDialog from './components/ErrorDialog.vue';

const icons = ref([
  {
    id: 1,
    label: 'My Computer',
    icon: 'https://win98icons.alexmeub.com/icons/png/computer_explorer-4.png',
    icon_small: 'https://win98icons.alexmeub.com/icons/png/computer_explorer-1.png',
    x: 70,
    y: 70,
    selected: false,
    delete() {
      deleteIcon(this);
    }
  },
  {
    id: 2,
    label: 'Trash',
    icon: 'https://win98icons.alexmeub.com/icons/png/recycle_bin_empty-4.png',
    icon_small: 'https://win98icons.alexmeub.com/icons/png/recycle_bin_empty-1.png',
    x: 70,
    y: 140,
    selected: false,
  },
  {
    id: 3,
    label: 'Paint',
    icon: 'https://win98icons.alexmeub.com/icons/png/paint_old-0.png',
    icon_small: 'https://win98icons.alexmeub.com/icons/png/paint_file-2.png',
    x: 140,
    y: 70,
    selected: false,
    delete() {
      deleteIcon(this);
    }
  },
  {
    id: 4,
    label: 'Notepad',
    icon: 'https://win98icons.alexmeub.com/icons/png/notepad-0.png',
    icon_small: 'https://win98icons.alexmeub.com/icons/png/notepad-1.png',
    x: 210,
    y: 70,
    selected: false,
    delete() {
      deleteIcon(this);
    }
  },
  {
    id: 5,
    label: 'Minesweeper',
    icon: 'https://win98icons.alexmeub.com/icons/png/minesweeper-0.png',
    icon_small: 'https://win98icons.alexmeub.com/icons/png/minesweeper-1.png',
    x: 140,
    y: 140,
    selected: false,
    delete() {
      deleteIcon(this);
    }
  },
  {
    id: 7,
    label: 'Plugins',
    icon: 'https://www.spigotmc.org/favicon.ico',
    x: 140,
    y: 280,
    shortcut: true,
  },
  {
    id: 8,
    label: 'PinBall',
    icon: 'apps/pinball/pinball-icon.png',
    icon_small: 'apps/pinball/pinball-icon.png',
    x: 210,
    y: 140,
    selected: false,
    delete() {
      deleteIcon(this);
    }
  },
  {
    id: 6,
    label: 'GitHub',
    icon: 'https://github.githubassets.com/favicons/favicon.png',
    x: 70,
    y: 280,
    shortcut: true,
  },
  {
    id: 9,
    label: 'matteodev.it',
    icon: 'https://matteodev.it/logo.svg',
    x: 210,
    y: 280,
    shortcut: true,
  },
  {
    id: 10,
    label: 'Steam',
    icon: 'https://store.steampowered.com/favicon.ico',
    x: 280,
    y: 280,
    shortcut: true,
  },
  {
    id: 11,
    label: 'ItemsAdder',
    icon: 'https://itemsadder.com/assets/images/favicon.webp',
    x: 350,
    y: 280,
    shortcut: true,
    delete() {
      deleteIcon(this);
    }
  },
]);

const appProperties = {
  3: {
    content: {
      iframe: 'apps/jspaint/index.html',
    },
    loadDelay: {
      min: 200,
      max: 500,
    },
    margin: 0,
    width: 800,
    height: 800
  },
  4: {
    content: {
      iframe: 'apps/notepad/index.html',
    },
    loadDelay: {
      min: 100,
      max: 300,
    },
    margin: 0,
    width: 500,
    height: 500
  },
  5: {
    content: {
      iframe: 'apps/minesweeper/index.html',
    },
    loadDelay: {
      min: 500,
      max: 1000,
    },
    margin: 0,
    width: 290,
    height: 380,
  },
  8: {
    content: {
      iframe: 'apps/pinball/space-cadet.html',
    },
    loadDelay: {
      min: 1000,
      max: 2500,
      sound: "/hdd.mp3",
    },
    margin: 0,
    width: 603,
    height: 480,
  },
  6: {
    content: {
      link: `https://github.com/LoneDev6`,
    },
    margin: 0,
    width: 900,
    height: 900,
  },
  7: {
    content: {
      iframe: `https://plugins.devs.beer`,
    },
    margin: 0,
    width: 900,
    height: 900,
  },
  9: {
    content: {
      link: 'https://www.matteodev.it',
    },
    margin: 0,
    width: 800,
    height: 600,
  },
  10: {
    content: {
      link: 'https://store.steampowered.com/developer/LoneDev',
    },
    margin: 0,
    width: 800,
    height: 600,
  },
  11: {
    content: {
      iframe: 'https://itemsadder.com/',
    },
    margin: 0,
    width: 1000,
    height: 900,
  },
};

const openWindows = ref([]);
const activeWindowId = ref(null);
const windowRefs = ref({}); 

const contextMenu = ref({ visible: false, x: 0, y: 0, icon: null });
const confirmDialog = ref({
  visible: false,
  message: '',
  onConfirm: null,
});

const showErrorDialog = ref(false);
const errorMessage = ref('');

function showNotOsErrorDialog() {
  errorMessage.value = 'This is not a real OS! :-)';
  showErrorDialog.value = true;
}

function setWindowRef(id) {
  return function (el) {
    if (el) {
      windowRefs.value[id] = el;
    } else {
      delete windowRefs.value[id];
    }
  };
}

function bringToFront(id) {
  const winRef = windowRefs.value[id];
  if (winRef && winRef.bringToFront) {
    winRef.bringToFront();
  }

  const win = openWindows.value.find(w => w.id === id);
  if (win) {
    // If the window is minimized, make it visible
    win.minimized = false;
    win.closed = false;

    // Move the window to the front
    activeWindowId.value = id;
  }
}

function onSelectionChange(selectedIds) {
  icons.value.forEach(icon => {
    icon.selected = selectedIds.includes(icon.id);
  });
}

function showContextMenu(icon, event) {
  contextMenu.value = {
    visible: true,
    x: event.clientX,
    y: event.clientY,
    icon,
  };
}

function closeContextMenu() {
  contextMenu.value.visible = false;
}

function openApp(icon) {

  // Skip if already open
  if (openWindows.value.some(w => w.id === icon.id)) {
    bringToFront(icon.id);
    return;
  }

  if (icon.id === 1 || icon.id === 2) {
    // Show the error dialog
    errorMessage.value = 'This is not a real OS! :-)';
    showErrorDialog.value = true;
  } else {

    const app = appProperties[icon.id];
    if(!app) {
      errorMessage.value = 'Missing app properties!';
      showErrorDialog.value = true;
      return;
    }

    // Set the cursor to "loading"
    document.body.style.cursor = 'url("/cur_load.png"), auto';

    // Random delay
    let delay = 0;
    if(app.loadDelay) {
      delay = Math.floor(Math.random() * (app.loadDelay.max - app.loadDelay.min + 1)) + app.loadDelay.min;
      let sound;
      if(app.loadDelay.sound && delay >= 1500) {
        sound = new Audio(app.loadDelay.sound);
        sound.play();
      }

      // At random points revert to default cursor, intermittently
      // This simulates a loading effect
      let loadingInterval = setInterval(() => {
        if (Math.random() > 0.75) {
          return;
        }
        document.body.style.cursor =
          document.body.style.cursor === 'url("/cur_load.png"), auto'
            ? 'url(/cursor.png), auto'
            : 'url("/cur_load.png"), auto';
      }, 50);
      setTimeout(() => {
        clearInterval(loadingInterval);

        if (sound) {
          const fadeOutInterval = setInterval(() => {
            if (sound.volume > 0.1) {
              sound.volume -= 0.1;
            } else {
              sound.pause();
              sound.currentTime = 0;
              clearInterval(fadeOutInterval);
            }
          }, 250);
        }
      }, delay);
    }

    setTimeout(() => {
      // Reset the cursor to default
      document.body.style.cursor = 'url(/cursor.png), auto';

      const exists = openWindows.value.find(w => w.id === icon.id);
      if (!exists) {
        if (appProperties[icon.id]?.content?.link) {
          window.open(appProperties[icon.id].content.link, '_blank');
          return;
        }

        // Calculate the offset for the new window
        const offset = 30;
        const baseX = window.innerWidth / 2 - (appProperties[icon.id]?.width || 300) / 2;
        const baseY = window.innerHeight / 2 - (appProperties[icon.id]?.height || 200) / 2;

        const newX = baseX + (openWindows.value.length * offset) % (window.innerWidth - (appProperties[icon.id]?.width || 300));
        const newY = baseY + (openWindows.value.length * offset) % (window.innerHeight - (appProperties[icon.id]?.height || 200));

        const appProps = appProperties[icon.id] || {
          content: {
            html: '<p>Content not available</p>',
          },
          margin: 10,
        };

        openWindows.value.push({
          id: icon.id,
          label: icon.id == 3 ? 'untitled - Paint' : icon.label,
          icon: icon.icon_small || icon.icon,
          minimized: false,
          closed: false,
          x: newX,
          y: newY,
          width: 300,
          height: 200,
          ...appProps,
        });
      } else {
        exists.minimized = false;
        exists.closed = false;
      }
      activeWindowId.value = icon.id;

      // Deselect the icon after opening the app
      const selectedIcon = icons.value.find(i => i.id === icon.id);
      if (selectedIcon) {
        selectedIcon.selected = false;
      }
    }, delay);
  }
}

function closeApp(id) {
  const win = openWindows.value.find(w => w.id === id);
  if (win) {
    win.closed = true;
    // Remove the window from openWindows
    openWindows.value = openWindows.value.filter(w => w.id !== id);

    // Remove the DOM reference
    delete windowRefs.value[id];
  }
}

function toggleWindow(id) {
  const win = openWindows.value.find(w => w.id === id);
  if (win) {
    win.minimized = !win.minimized;
    win.closed = false;
    activeWindowId.value = id;
  }
}

function deleteIcon(icon) {
  console.log('Deleting icon:', icon);
  icons.value = icons.value.filter(i => i.id !== icon.id);
}

function showConfirmDialog({ message, onConfirm }) {
  confirmDialog.value = {
    visible: true,
    message,
    onConfirm,
  };
}

function handleConfirmDialog() {
  if (confirmDialog.value.onConfirm) {
    confirmDialog.value.onConfirm();
  }
  confirmDialog.value.visible = false;
}

function closeConfirmDialog() {
  confirmDialog.value.visible = false;
}

function closeErrorDialog() {
  showErrorDialog.value = false;
}

onMounted(() => {
  // Set the default cursor on page load
  document.body.style.cursor = 'url(/cursor.png), auto';

  // Disable right-click globally
  document.addEventListener('contextmenu', disableRightClick);
});

onBeforeUnmount(() => {
  // Clean up the event listener
  document.removeEventListener('contextmenu', disableRightClick);
});

function disableRightClick(event) {
  event.preventDefault();
}
</script>

<style>
.desktop {
  width: 100vw;
  height: 100vh;
  background: #008080;
  position: relative;
  overflow: hidden;
}
</style>
